/**
 * Cytoscape's node drawing caches the OUTLINE path in `rscratch.pathCache`
 * after the node's own path, and `drawInscribedImage` clips to that cache. A
 * node with an outline (the selection ring) therefore clips its image to the
 * larger outline shape and the image spills past the border. This rebuilds the
 * node's own shape path for the image clip while an outline is drawn.
 */

type CornerRadius = number | 'auto';

interface ShapeImpl {
  draw(
    target: Path2D,
    centerX: number,
    centerY: number,
    width: number,
    height: number,
    cornerRadius: CornerRadius,
    rs: unknown,
  ): void;
}

interface DrawnNode {
  _private?: { rscratch?: { pathCache?: Path2D | null } };
  pstyle(name: string): { value?: unknown; pfValue?: number } | undefined;
  width(): number;
  height(): number;
  padding(): number;
}

type DrawInscribedImage = (
  context: CanvasRenderingContext2D,
  img: unknown,
  node: DrawnNode,
  ...rest: unknown[]
) => unknown;

interface PatchableRenderer {
  drawInscribedImage?: DrawInscribedImage;
  nodeShapes?: Record<string, ShapeImpl | undefined>;
  getNodeShape?: (node: DrawnNode) => string;
  __maporiumNodeImageClip?: boolean;
}

function ownShapePath(renderer: PatchableRenderer, node: DrawnNode, rs: unknown): Path2D | null {
  if (typeof Path2D === 'undefined' || typeof renderer.getNodeShape !== 'function') return null;
  const shape = renderer.nodeShapes?.[renderer.getNodeShape(node)];
  if (!shape) return null;
  const radius = node.pstyle('corner-radius');
  const cornerRadius: CornerRadius = radius?.value === 'auto' ? 'auto' : Number(radius?.pfValue) || 0;
  const padding = node.padding() * 2;
  const path = new Path2D();
  shape.draw(path, 0, 0, node.width() + padding, node.height() + padding, cornerRadius, rs);
  return path;
}

export function patchNodeImageClip(renderer: unknown): boolean {
  const target = renderer as PatchableRenderer | null | undefined;
  if (!target || typeof target.drawInscribedImage !== 'function') return false;
  if (target.__maporiumNodeImageClip) return false;

  const original = target.drawInscribedImage;
  target.drawInscribedImage = function (
    this: PatchableRenderer,
    context: CanvasRenderingContext2D,
    img: unknown,
    node: DrawnNode,
    ...rest: unknown[]
  ) {
    const rs = node?._private?.rscratch;
    const cached = rs?.pathCache;
    if (!rs || !cached || !(Number(node.pstyle('outline-width')?.pfValue) > 0)) {
      return original.call(this, context, img, node, ...rest);
    }
    const own = ownShapePath(this, node, rs);
    if (!own) return original.call(this, context, img, node, ...rest);
    rs.pathCache = own;
    try {
      return original.call(this, context, img, node, ...rest);
    } finally {
      rs.pathCache = cached;
    }
  };
  target.__maporiumNodeImageClip = true;
  return true;
}

export function installNodeImageClip(cy: unknown): boolean {
  const core = cy as { renderer?: () => unknown } | null | undefined;
  if (!core || typeof core.renderer !== 'function') return false;
  return patchNodeImageClip(core.renderer());
}
